import React, { useEffect, useMemo, useRef, useState } from 'react';
import config from '../../config';
import s from './Leads.module.scss';

export const emptyPricingSnapshot = (lead = {}) => ({
  customer_expectation_rate: lead.customer_expectation_rate ?? '',
  offered_rate: lead.offered_rate ?? '',
  best_transporter_rate: lead.best_transporter_rate ?? '',
  best_transporter_id: lead.best_transporter_id ?? null,
  best_transporter_name: lead.best_transporter_name ?? '',
});

export const normalizePricingSnapshot = (value = {}) => {
  const normalized = {};
  ['customer_expectation_rate', 'offered_rate', 'best_transporter_rate'].forEach((field) => {
    const raw = value[field];
    normalized[field] = raw === '' || raw == null ? null : Number(raw);
  });
  normalized.best_transporter_id = value.best_transporter_id || null;
  normalized.best_transporter_name = String(value.best_transporter_name || '').trim() || null;
  return normalized;
};

const PricingSnapshotFields = ({ value, onChange, compact = false }) => {
  const [transporters, setTransporters] = useState([]);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const closeTimer = useRef(null);
  const transporterQuery = value.best_transporter_name || '';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${config.opsApiBase}/transporters`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load transporters'))))
      .then((data) => {
        if (!cancelled) setTransporters(data.transporters || []);
      })
      .catch(() => {
        if (!cancelled) setTransporters([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      clearTimeout(closeTimer.current);
    };
  }, []);

  const recommendations = useMemo(() => {
    const terms = transporterQuery.toLowerCase().trim().split(/\s+/).filter(Boolean);
    const ranked = transporters
      .map((transporter) => {
        const label = transporter.agency_name || transporter.contact_name || transporter.user_name || `Transporter #${transporter.id}`;
        const searchable = [
          label,
          transporter.contact_name,
          transporter.user_name,
          transporter.phone_number,
          transporter.user_phone,
          ...(Array.isArray(transporter.routes)
            ? transporter.routes.flatMap((route) => [
                route?.from_place?.address,
                route?.to_place?.address,
              ])
            : []),
        ].filter(Boolean).join(' ').toLowerCase();
        const score = terms.reduce((total, term) => {
          if (label.toLowerCase().startsWith(term)) return total + 4;
          if (label.toLowerCase().includes(term)) return total + 3;
          return searchable.includes(term) ? total + 1 : total;
        }, 0);
        return { transporter, label, score };
      })
      .filter((item) => terms.length === 0 || item.score > 0)
      .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
    return ranked.slice(0, 6);
  }, [transporterQuery, transporters]);

  const setField = (field, nextValue) => onChange({ ...value, [field]: nextValue });

  return (
    <section className={`${s.pricingSnapshot} ${compact ? s.pricingSnapshotCompact : ''}`}>
      <div className={s.pricingSnapshotHeader}>
        <div>
          <strong>Pricing snapshot</strong>
          <span>Optional · total trip rates in INR</span>
        </div>
        <i className="fa fa-line-chart" />
      </div>
      <div className={s.pricingRateGrid}>
        {[
          ['customer_expectation_rate', 'Customer expected', 'What price did the customer expect?'],
          ['offered_rate', 'Our offer', 'What did Unloadin quote?'],
          ['best_transporter_rate', 'Best transporter', 'Lowest usable transporter quote'],
        ].map(([field, label, hint]) => (
          <label className={s.pricingField} key={field}>
            <span>{label}</span>
            <div className={s.currencyInput}>
              <b>₹</b>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="decimal"
                value={value[field] ?? ''}
                placeholder="0"
                title={hint}
                onChange={(event) => setField(field, event.target.value)}
              />
            </div>
          </label>
        ))}
      </div>
      <label className={s.pricingField}>
        <span>Who gave the best transporter rate?</span>
        <div className={s.transporterSearch}>
          <i className="fa fa-search" />
          <input
            type="text"
            value={transporterQuery}
            placeholder={loading ? 'Loading transporters…' : 'Type agency, contact, phone or route'}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              closeTimer.current = setTimeout(() => setFocused(false), 150);
            }}
            onChange={(event) => onChange({
              ...value,
              best_transporter_id: null,
              best_transporter_name: event.target.value,
            })}
          />
          {value.best_transporter_id && <i className={`fa fa-check-circle ${s.transporterSelected}`} />}
          {focused && recommendations.length > 0 && (
            <div className={s.transporterRecommendations}>
              {recommendations.map(({ transporter, label }) => (
                <button
                  type="button"
                  key={transporter.id}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange({
                      ...value,
                      best_transporter_id: transporter.id,
                      best_transporter_name: label,
                    });
                    setFocused(false);
                  }}
                >
                  <i className="fa fa-truck" />
                  <span>
                    <strong>{label}</strong>
                    <small>
                      {[transporter.contact_name, transporter.phone_number || transporter.user_phone]
                        .filter(Boolean).join(' · ')}
                    </small>
                  </span>
                  <em>#{transporter.id}</em>
                </button>
              ))}
            </div>
          )}
        </div>
        <small className={s.pricingHelp}>
          Pick a recommendation to link its ID, or leave a typed name for an unregistered transporter.
        </small>
      </label>
    </section>
  );
};

export default PricingSnapshotFields;
